import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Icons,
  Input,
  List,
  Row,
  Select,
  Space,
  Table,
  Tooltip,
  TextField,
  useTable,
} from "@pankod/refine-antd";
import { HttpError, IResourceComponentsProps } from "@pankod/refine-core";
import { useState } from "react";

import { AuditActorCell } from "./components/audit-actor-cell";
import { AuditActionTag } from "./components/audit-action-tag";
import { AuditLogDetailModal } from "./components/audit-log-detail-modal";
import { AuditMethodTag } from "./components/audit-method-tag";
import { AuditResultTag } from "./components/audit-result-tag";
import "./styles/audit-log.css";
import { AuditEvent, AuditFilter } from "./types";
import {
  AUDIT_ALL_ACTION_OPTION,
  AUDIT_ALL_RESOURCE_OPTION,
  AUDIT_LOG_PAGINATION_DEFAULTS,
  AUDIT_LOG_RESOURCE,
  AUDIT_LOG_TEXTS,
} from "./utils/audit-log.constants";
import { useAuditLogMetadata } from "./hooks/use-audit-log-metadata";
import { buildAuditLogFilters, isInvalidAuditDateRange } from "./utils/audit-log.filters";
import {
  buildResourceLabelMap,
  formatAuditDate,
  formatAuditTime,
  getAuditEventId,
  getResourceLabel,
  getSafeText,
} from "./utils/audit-log.helpers";

const listTitle = (
  <div className="audit-log-heading">
    <div>{AUDIT_LOG_TEXTS.title}</div>
    <div className="audit-log-heading-description">
      {AUDIT_LOG_TEXTS.description}
    </div>
  </div>
);

const TruncatedText: React.FC<{ value?: string | number | null }> = ({
  value,
}) => {
  const text = getSafeText(value);

  return (
    <Tooltip title={text === "-" ? undefined : text}>
      <span className="audit-log-truncated-text">{text}</span>
    </Tooltip>
  );
};

export const AuditLogList: React.FC<IResourceComponentsProps> = () => {
  const [selectedEvent, setSelectedEvent] = useState<AuditEvent | null>(null);
  const [showResults, setShowResults] = useState(true);

  const { data: metadataResponse, isLoading: isMetadataLoading } =
    useAuditLogMetadata();

  const metadata = metadataResponse?.data;

  const resourceOptions = [
    AUDIT_ALL_RESOURCE_OPTION,
    ...(metadata?.resources || []),
  ];

  const actionOptions = [
    AUDIT_ALL_ACTION_OPTION,
    ...(metadata?.actions || []),
  ];

  const resourceLabels = buildResourceLabelMap(metadata?.resources || []);

  const handleSearch = (values: AuditFilter) => {
    setShowResults(true);
    return buildAuditLogFilters(values);
  };

  const { tableProps, searchFormProps, tableQueryResult } = useTable<
    AuditEvent,
    HttpError,
    AuditFilter
  >({
    resource: AUDIT_LOG_RESOURCE,
    defaultSetFilterBehavior: "replace",
    initialCurrent: AUDIT_LOG_PAGINATION_DEFAULTS.initialCurrent,
    initialPageSize: AUDIT_LOG_PAGINATION_DEFAULTS.initialPageSize,
    hasPagination: AUDIT_LOG_PAGINATION_DEFAULTS.hasPagination,
    syncWithLocation: false,
    onSearch: handleSearch,
    queryOptions: {
      enabled: true,
    },
  });

  const handleCleanFilters = () => {
    searchFormProps.form?.resetFields();
    setShowResults(false);
  };

  const validateDateRange = () => {
    const values = searchFormProps.form?.getFieldsValue() as AuditFilter;

    if (isInvalidAuditDateRange(values || {})) {
      return Promise.reject(new Error(AUDIT_LOG_TEXTS.invalidDateRange));
    }

    return Promise.resolve();
  };

  const isTableLoading = Boolean(
    tableQueryResult.isLoading || tableQueryResult.isFetching,
  );

  const tablePagination = {
    ...tableProps.pagination,
    showTotal: (total: number, range: [number, number]) =>
      `${range[0]}-${range[1]} de ${total} registros`,
  };

  return (
    <List
      title={listTitle}
      canCreate={false}
      headerButtons={() => null}
    >
      <Card className="audit-log-filter-card" title={AUDIT_LOG_TEXTS.filtersTitle}>
        <Form {...searchFormProps} layout="vertical">
          <Row gutter={[16, 16]}>
            <Col xs={24} md={8}>
              <Form.Item name="actor" label={AUDIT_LOG_TEXTS.actorLabel}>
                <Input placeholder={AUDIT_LOG_TEXTS.actorPlaceholder} />
              </Form.Item>
            </Col>

            <Col xs={24} md={8}>
              <Form.Item name="resource" label={AUDIT_LOG_TEXTS.resourceLabel}>
                <Select
                  loading={isMetadataLoading}
                  options={resourceOptions}
                  placeholder={AUDIT_LOG_TEXTS.resourcePlaceholder}
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={8}>
              <Form.Item name="action" label={AUDIT_LOG_TEXTS.actionLabel}>
                <Select
                  loading={isMetadataLoading}
                  options={actionOptions}
                  placeholder={AUDIT_LOG_TEXTS.actionPlaceholder}
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={8}>
              <Form.Item name="entity_id" label={AUDIT_LOG_TEXTS.entityLabel}>
                <Input placeholder={AUDIT_LOG_TEXTS.entityPlaceholder} />
              </Form.Item>
            </Col>

            <Col xs={24} md={8}>
              <Form.Item
                name="date_from"
                label={AUDIT_LOG_TEXTS.startDateLabel}
                dependencies={["date_to"]}
                rules={[{ validator: validateDateRange }]}
              >
                <DatePicker
                  format="DD/MM/YYYY"
                  placeholder="dd/mm/yyyy"
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={8}>
              <Form.Item
                name="date_to"
                label={AUDIT_LOG_TEXTS.endDateLabel}
                dependencies={["date_from"]}
                rules={[{ validator: validateDateRange }]}
              >
                <DatePicker
                  format="DD/MM/YYYY"
                  placeholder="dd/mm/yyyy"
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>

            <Col xs={24} className="audit-log-filter-actions">
              <Space>
                <Button icon={<Icons.ClearOutlined />} onClick={handleCleanFilters}>
                  {AUDIT_LOG_TEXTS.clearButton}
                </Button>
                <Button
                  htmlType="submit"
                  icon={<Icons.SearchOutlined />}
                  loading={isTableLoading}
                  type="primary"
                >
                  {AUDIT_LOG_TEXTS.searchButton}
                </Button>
              </Space>
            </Col>
          </Row>
        </Form>
      </Card>

      {tableQueryResult.isError && (
        <div className="audit-log-error" role="alert">
          {AUDIT_LOG_TEXTS.errorText}
        </div>
      )}

      <Table
        {...tableProps}
        className="audit-log-table"
        dataSource={showResults ? tableProps.dataSource : []}
        rowKey={(record) => getAuditEventId(record)}
        loading={showResults ? isTableLoading : false}
        pagination={showResults ? tablePagination : false}
        scroll={{ x: 1280 }}
        locale={{ emptyText: AUDIT_LOG_TEXTS.emptyText }}
      >
        <Table.Column<AuditEvent>
          dataIndex="at"
          title="Data / hora"
          width={130}
          render={(value) => (
            <span className="audit-log-date-cell">
              <TextField strong value={formatAuditDate(value)} />
              <span className="audit-log-secondary-text">
                {formatAuditTime(value)}
              </span>
            </span>
          )}
        />
        <Table.Column<AuditEvent>
          dataIndex="actor_id"
          title="Ator"
          width={220}
          render={(_, record) => (
            <AuditActorCell
              actorId={record.actor_id}
              actorName={record.actor_name}
            />
          )}
        />
        <Table.Column<AuditEvent>
          dataIndex="action"
          title="Ação"
          width={160}
          render={(value, record) => <AuditActionTag action={value || record.method} />}
        />
        <Table.Column<AuditEvent>
          dataIndex="resource"
          title="Recurso"
          width={220}
          render={(value, record) => (
            <span className="audit-log-cell-stack">
              <TextField strong value={getResourceLabel(value, resourceLabels)} />
              <TruncatedText value={record.entity_id} />
            </span>
          )}
        />
        <Table.Column<AuditEvent>
          dataIndex="path"
          title="Requisição"
          width={360}
          render={(value, record) => (
            <Space size={8} className="audit-log-request-cell">
              <AuditMethodTag method={record.method} />
              <TruncatedText value={value} />
            </Space>
          )}
        />
        <Table.Column<AuditEvent>
          dataIndex="outcome"
          title="Resultado"
          width={170}
          render={(value, record) => (
            <AuditResultTag outcome={value} status={record.status} />
          )}
        />
        <Table.Column<AuditEvent>
          title=""
          width={72}
          render={(_, record) => (
            <Tooltip title="Ver detalhes">
              <Button
                aria-label="Ver detalhes"
                icon={<Icons.EyeOutlined />}
                size="small"
                onClick={() => setSelectedEvent(record)}
              />
            </Tooltip>
          )}
        />
      </Table>

      <AuditLogDetailModal
        event={selectedEvent}
        resourceLabels={resourceLabels}
        visible={Boolean(selectedEvent)}
        onClose={() => setSelectedEvent(null)}
      />
    </List>
  );
};
